import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, OnInit } from "@angular/core";
import { IncidentEmailsService } from "../services/incident-emails.service";
import { IncidentEmailModel } from "../models/incident-email.model";
import { MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatSortModule, Sort, SortDirection } from "@angular/material/sort";
import { EmailDetailsModel } from "./sent-email-details/models/email-details.model";
import { SentEmailDetailsComponent } from "./sent-email-details/sent-email-details.component";
import { DateTimeDisplayPipe } from "@app/shared/pipes/date-time-display.pipe";
import { IncidentEmailStatus } from "../models/incident-email-status.enum";
import { MatIconModule } from "@angular/material/icon";
import { PageNumberSelectorComponent } from "./paginator/page-number-selector/page-number-selector.component";
import { CerrixPaginatorComponent } from "./paginator/cerrix-paginator/cerrix-paginator.component";
import { debounce, filter, Subject, timer } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { CerrixInputComponent } from "../components/src/lib/core/cerrix-input/cerrix-input.component";
import { CerrixTitleComponent } from "../components/src/lib/core/cerrix-title/cerrix-title.component";
import { CerrixDialogService } from "../components/src/lib/core/services/cerrix-dialog.service";

@Component({
    selector: "incident-sent-emails",
    templateUrl: "./incident-sent-emails.component.html",
    styleUrls: ["./incident-sent-emails.component.scss"],
    imports: [
        CommonModule,
        MatTableModule,
        MatSortModule,
        MatTooltipModule,
        MatIconModule,
        FormsModule,
        ReactiveFormsModule,
        PageNumberSelectorComponent,
        CerrixPaginatorComponent,
        DateTimeDisplayPipe,
        CerrixInputComponent,
        CerrixTitleComponent,
    ],
    providers: [],
})
export class IncidentSentEmailsComponent implements OnInit {
    protected readonly incidentEmailStatus: typeof IncidentEmailStatus = IncidentEmailStatus;

    private readonly incidentEmailsService = inject(IncidentEmailsService);
    private readonly dialogService = inject(CerrixDialogService);
    private readonly destroyRef = inject(DestroyRef);

    protected pageIndex: number = 0;
    protected pageSize: number = 25;
    protected sortActive: string = "DateSent";
    protected sortDirection: SortDirection = "desc";
    protected displayedColumns: string[] = ["DateSent", "Subject", "To", "EmailStatus"];
    protected tooltipDelay: number = 300;

    protected searchValue: string = "";
    private lastSearchValue: string = "";
    private search$ = new Subject<{
        force?: boolean;
    }>();
    private debounceSearch: number = 300;

    private loadingEmails = false;

    protected totalEmails: number = 0;
    protected emails: IncidentEmailModel[] = [];

    ngOnInit(): void {
        this.loadEmails();

        this.search$
            .pipe(
                debounce((search) => (search?.force ? timer(0) : timer(this.debounceSearch))),
                filter(
                    (search) =>
                        search.force ||
                        (this.searchValue !== this.lastSearchValue &&
                            (!this.searchValue || this.searchValue.length >= 3)),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => {
                this.pageIndex = 0;
                this.loadEmails();
            });
    }

    protected onSearch(onEnter: boolean) {
        if (!this.loadingEmails && this.lastSearchValue !== this.searchValue) {
            this.search$.next({
                force: !!onEnter,
            });
        }
    }

    private loadEmails() {
        this.loadingEmails = true;
        this.lastSearchValue = this.searchValue;

        this.incidentEmailsService
            .getEmails(
                this.pageSize * this.pageIndex,
                this.pageSize,
                this.sortActive,
                this.sortDirection,
                this.searchValue,
            )
            .subscribe((pagedEmails) => {
                this.loadingEmails = false;

                this.totalEmails = pagedEmails.total;
                this.emails = pagedEmails.data;
            });
    }

    protected onPageIndexChanged(pageIndex: number) {
        this.pageIndex = pageIndex;

        this.loadEmails();
    }

    protected onPageNumberChanged(pageSize: number) {
        this.pageIndex = 0;
        this.pageSize = pageSize;

        this.loadEmails();
    }

    protected onSortChange(sortState: Sort) {
        this.sortActive = sortState.active;
        this.sortDirection = sortState.direction;

        this.pageIndex = 0;

        this.loadEmails();
    }

    protected openEmail(email: IncidentEmailModel) {
        var promptUiid = "sent-email-details-popup";
        const popupData: EmailDetailsModel = {
            recipient: email.to,
            subject: email.subject,
            body: email.body,
            error: email.error,
        };

        this.dialogService.openDialog({
            width: "826px",
            data: {
                component: SentEmailDetailsComponent,
                componentData: {
                    email: popupData,
                    dataUiid: promptUiid,
                },
                promptUiid: promptUiid,
                title: "Sent email",
                negativeButton: {
                    text: "Close",
                    buttonClass: "cancel",
                    onAction: () => {
                        return true;
                    },
                },
            },
        });
    }
}
